import { Cubit } from "blac-next";
import { EmblaCarouselType } from "embla-carousel";

export interface CarouselCubitState {
  selectedIndex: number;
  scrollSnaps: number[];
  canScrollPrev: boolean;
  canScrollNext: boolean;
  totalSlides: number;
}

export type ScrollProgressCallback = (progress: number) => void;

export interface CaourselStepChangeDetails {
  stepName?: string;
  carouselId?: string;
  carouselLabel?: string;
}

export interface CarouselCubitProps {
  id?: string;
  label?: string;
  onStepChange?: (index: number, details: CaourselStepChangeDetails) => void;
}

export default class CarouselCubit extends Cubit<CarouselCubitState> {
  private emblaApi: EmblaCarouselType | undefined;
  private progressCallbacks = new Set<ScrollProgressCallback>();
  private currentProgress = 0;
  private slideNames: (string | undefined)[] = [];

  constructor() {
    super({
      selectedIndex: 0,
      scrollSnaps: [],
      canScrollPrev: false,
      canScrollNext: true,
      totalSlides: 0
    });
  }

  subscribeToProgress = (callback: ScrollProgressCallback): (() => void) => {
    this.progressCallbacks.add(callback);
    callback(this.currentProgress);
    return () => {
      this.progressCallbacks.delete(callback);
    };
  };

  getScrollProgress = (): number => {
    return this.currentProgress;
  };

  setTotalSlides = (count: number) => {
    if (this.state.totalSlides !== count) {
      this.patch({ totalSlides: count });
    }
  };

  setSlideNames = (names: (string | undefined)[]) => {
    this.slideNames = names;
  };

  setEmblaApi = (api: EmblaCarouselType | undefined) => {
    if (!api) {
      return;
    }
    if (api === this.emblaApi) {
      return;
    }

    this.emblaApi = api;

    const onInit = () => {
      this.patch({
        scrollSnaps: api.scrollSnapList()
      });
    };

    const onSelect = () => {
      const newIndex = api.selectedScrollSnap();
      const prevIndex = this.state.selectedIndex;
      this.patch({
        selectedIndex: newIndex,
        canScrollPrev: api.canScrollPrev(),
        canScrollNext: api.canScrollNext()
      });
      if (newIndex !== prevIndex) {
        this.customProps.onStepChange?.(newIndex, {
          stepName: this.slideNames[newIndex],
          carouselId: this.customProps.id,
          carouselLabel: this.customProps.label
        });
      }
    };

    const onScroll = () => {
      this.currentProgress = api.scrollProgress();
      this.progressCallbacks.forEach((cb) => cb(this.currentProgress));
    };

    onInit();
    onSelect();
    onScroll();

    api
      .on("reInit", onInit)
      .on("reInit", onSelect)
      .on("select", onSelect)
      .on("scroll", onScroll);
  };

  scrollTo = (index: number) => {
    this.emblaApi?.scrollTo(index);
  };

  scrollPrev = () => {
    this.emblaApi?.scrollPrev();
  };

  scrollNext = () => {
    this.emblaApi?.scrollNext();
  };

  customProps: CarouselCubitProps = {};
  setProps = (props: CarouselCubitProps) => {
    this.customProps = props;
  };

  get isFirstSlide() {
    return this.state.selectedIndex === 0;
  }

  get isLastSlide() {
    return this.state.selectedIndex === this.state.scrollSnaps.length - 1;
  }
}
